import { FormEvent, ReactNode, useCallback, useEffect, useState } from 'react'
import { LEGAL_INFO_ROUTE } from '../../app/router/routes'
import { useAppSelector } from '../../app/store/store'
import { IAdminDomain, IAdminDomainPatch, ILegalInfo, ILegalInfoPatch, legalInfoService, TAdminDomainStatus } from '../../entities/legalInfo'
import { useGlobalMessageActions } from '../../entities/globalMessage'
import { FEATURES, hasFeature, useMyActions } from '../../entities/my'
import { AuthError } from '../../shared/lib/error/AuthError'
import { MyError } from '../../shared/lib/error/MyError'
import { AccessDeniedRedirect } from '../../shared/ui/accessDeniedRedirect'
import { MyButtonSecondary } from '../../shared/ui/button'
import { MyInput } from '../../shared/ui/input'
import { MyTextarea } from '../../shared/ui/myTextarea'
import { SurfaceBlock } from '../../shared/ui/surfaceBlock'
import { LegalDocumentsWidget } from '../../widgets/legalDocuments'
import { LayoutPages } from '../layoutPages'
import classes from './legalInfo.module.scss'

type TLegalInfoForm = Pick<ILegalInfo, 'fullName' | 'inn' | 'ogrnip' | 'registrationAddress' | 'email'>

type TDomainForm = Pick<IAdminDomain,
    | 'hostname'
    | 'status'
    | 'ownershipVerifiedAt'
    | 'domainPaidUntil'
    | 'certificateIssuedAt'
    | 'certificateExpiresAt'
    | 'certificateProvider'
    | 'nginxConfigAppliedAt'
    | 'lastError'
>

const EMPTY_LEGAL_INFO: TLegalInfoForm = {
    fullName: '',
    inn: '',
    ogrnip: '',
    registrationAddress: '',
    email: '',
}

const EMPTY_DOMAIN: TDomainForm = {
    hostname: '',
    status: 'ADMIN_DOMAIN_STATUS_PENDING_VERIFICATION',
    ownershipVerifiedAt: '',
    domainPaidUntil: '',
    certificateIssuedAt: '',
    certificateExpiresAt: '',
    certificateProvider: '',
    nginxConfigAppliedAt: '',
    lastError: '',
}

const DOMAIN_STATUS_LABELS: Record<TAdminDomainStatus, string> = {
    ADMIN_DOMAIN_STATUS_PENDING_VERIFICATION: 'Ожидает подтверждения',
    ADMIN_DOMAIN_STATUS_PROVISIONING: 'Настраивается',
    ADMIN_DOMAIN_STATUS_ACTIVE: 'Активен',
    ADMIN_DOMAIN_STATUS_FAILED: 'Ошибка',
    ADMIN_DOMAIN_STATUS_DISABLED: 'Отключён',
    ADMIN_DOMAIN_STATUS_EXPIRED: 'Истёк',
}

const DOMAIN_STATUS_OPTIONS = Object.entries(DOMAIN_STATUS_LABELS) as [TAdminDomainStatus, string][]

const toLegalForm = (info: ILegalInfo | null): TLegalInfoForm => info
    ? {
        fullName: info.fullName,
        inn: info.inn,
        ogrnip: info.ogrnip,
        registrationAddress: info.registrationAddress,
        email: info.email,
    }
    : {...EMPTY_LEGAL_INFO}

const utcToDateTimeInput = (value: string): string => {
    if (!value) return ''

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''

    const part = (number: number) => String(number).padStart(2, '0')
    return `${date.getFullYear()}-${part(date.getMonth() + 1)}-${part(date.getDate())}T${part(date.getHours())}:${part(date.getMinutes())}`
}

const toDomainForm = (domain: IAdminDomain | null): TDomainForm => domain
    ? {
        hostname: domain.hostname,
        status: domain.status,
        ownershipVerifiedAt: utcToDateTimeInput(domain.ownershipVerifiedAt),
        domainPaidUntil: domain.domainPaidUntil,
        certificateIssuedAt: utcToDateTimeInput(domain.certificateIssuedAt),
        certificateExpiresAt: utcToDateTimeInput(domain.certificateExpiresAt),
        certificateProvider: domain.certificateProvider,
        nginxConfigAppliedAt: utcToDateTimeInput(domain.nginxConfigAppliedAt),
        lastError: domain.lastError,
    }
    : {...EMPTY_DOMAIN}

const toUtc = (value: string): string => value ? new Date(value).toISOString() : ''

const formatDate = (value: string, withTime = false): string => {
    if (!value) return '—'

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value

    return new Intl.DateTimeFormat('ru-RU', withTime
        ? {day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'}
        : {day: '2-digit', month: '2-digit', year: 'numeric'}
    ).format(date)
}

const getLegalInfoPatch = (initial: ILegalInfo | null, form: TLegalInfoForm): ILegalInfoPatch => {
    if (!initial) return {...form}

    const patch: ILegalInfoPatch = {}
    if (form.fullName !== initial.fullName) patch.fullName = form.fullName
    if (form.inn !== initial.inn) patch.inn = form.inn
    if (form.ogrnip !== initial.ogrnip) patch.ogrnip = form.ogrnip
    if (form.registrationAddress !== initial.registrationAddress) patch.registrationAddress = form.registrationAddress
    if (form.email !== initial.email) patch.email = form.email
    return patch
}

const getDomainPatch = (initial: IAdminDomain | null, form: TDomainForm): IAdminDomainPatch => {
    if (!initial) {
        return {
            hostname: form.hostname,
            status: form.status,
            ownershipVerifiedAt: toUtc(form.ownershipVerifiedAt),
            domainPaidUntil: form.domainPaidUntil,
            certificateIssuedAt: toUtc(form.certificateIssuedAt),
            certificateExpiresAt: toUtc(form.certificateExpiresAt),
            certificateProvider: form.certificateProvider,
            nginxConfigAppliedAt: toUtc(form.nginxConfigAppliedAt),
            lastError: form.lastError,
        }
    }

    const patch: IAdminDomainPatch = {}
    if (form.hostname !== initial.hostname) patch.hostname = form.hostname
    if (form.status !== initial.status) patch.status = form.status
    if (form.ownershipVerifiedAt !== utcToDateTimeInput(initial.ownershipVerifiedAt)) patch.ownershipVerifiedAt = toUtc(form.ownershipVerifiedAt)
    if (form.domainPaidUntil !== initial.domainPaidUntil) patch.domainPaidUntil = form.domainPaidUntil
    if (form.certificateIssuedAt !== utcToDateTimeInput(initial.certificateIssuedAt)) patch.certificateIssuedAt = toUtc(form.certificateIssuedAt)
    if (form.certificateExpiresAt !== utcToDateTimeInput(initial.certificateExpiresAt)) patch.certificateExpiresAt = toUtc(form.certificateExpiresAt)
    if (form.certificateProvider !== initial.certificateProvider) patch.certificateProvider = form.certificateProvider
    if (form.nginxConfigAppliedAt !== utcToDateTimeInput(initial.nginxConfigAppliedAt)) patch.nginxConfigAppliedAt = toUtc(form.nginxConfigAppliedAt)
    if (form.lastError !== initial.lastError) patch.lastError = form.lastError
    return patch
}

const isEmpty = (value: object): boolean => Object.keys(value).length === 0

const ScalesIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 3V21M5 7H19M4 7L7 3H17L20 7M5.5 21H18.5" />
        <path d="M7.5 7L5.5 16.5C5.237 17.75 6.192 19 7.47 19H7.53C8.808 19 9.763 17.75 9.5 16.5L7.5 7ZM16.5 7L14.5 16.5C14.237 17.75 15.192 19 16.47 19H16.53C17.808 19 18.763 17.75 18.5 16.5L16.5 7Z" />
    </svg>
)

const DomainIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="8" />
        <path d="M4 12H20M12 4C14.4 6.19 15.77 9.29 15.77 12C15.77 14.71 14.4 17.81 12 20M12 4C9.6 6.19 8.23 9.29 8.23 12C8.23 14.71 9.6 17.81 12 20" />
    </svg>
)

const CollapseIcon = ({isOpen}: {isOpen: boolean}) => (
    <svg className={isOpen ? classes.collapseIconOpen : ''} viewBox="0 0 24 24" aria-hidden="true">
        <path d="M6 9L12 15L18 9" />
    </svg>
)

const Field = ({label, children, full = false, highlighted = false}: {label: string; children: ReactNode; full?: boolean; highlighted?: boolean}) => (
    <section className={`${classes.field} ${full ? classes.full : ''} ${highlighted ? classes.highlightedField : ''}`}>
        <span>{label}</span>
        <output>{children || '—'}</output>
    </section>
)

export default function LegalInfoPage() {
    const {my} = useAppSelector(state => state.myReducer)
    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()
    const canReadLegalInfo = hasFeature(my, FEATURES.LEGAL_INFO.READ)
    const canReadLegalDocuments = hasFeature(my, FEATURES.LEGAL_DOCUMENTS.READ)
    const canManage = hasFeature(my, FEATURES.LEGAL_INFO.MANAGE)
    const canOpen = canReadLegalInfo || canReadLegalDocuments

    const [legalInfo, setLegalInfo] = useState<ILegalInfo | null>(null)
    const [domain, setDomain] = useState<IAdminDomain | null>(null)
    const [legalForm, setLegalForm] = useState<TLegalInfoForm>(EMPTY_LEGAL_INFO)
    const [domainForm, setDomainForm] = useState<TDomainForm>(EMPTY_DOMAIN)
    const [isLegalLoading, setIsLegalLoading] = useState(true)
    const [isDomainLoading, setIsDomainLoading] = useState(true)
    const [isLegalSaving, setIsLegalSaving] = useState(false)
    const [isDomainSaving, setIsDomainSaving] = useState(false)
    const [isDomainSyncing, setIsDomainSyncing] = useState(false)
    const [isLegalEditing, setIsLegalEditing] = useState(false)
    const [isDomainEditing, setIsDomainEditing] = useState(false)
    const [isLegalOpen, setIsLegalOpen] = useState(false)
    const [isDomainOpen, setIsDomainOpen] = useState(false)

    const handleError = useCallback((error: unknown, fallback: string) => {
        if (error instanceof AuthError) {
            setIsAuth(false)
            setGlobalMessage({message: error.message, type: 'error'})
            return
        }

        if (error instanceof MyError) {
            setGlobalMessage({message: error.message || fallback, type: 'error'})
            return
        }

        setGlobalMessage({message: fallback, type: 'error'})
    }, [setGlobalMessage, setIsAuth])

    const loadLegalInfo = useCallback(async () => {
        try {
            setIsLegalLoading(true)
            const response = await legalInfoService.getLegalInfo()
            setLegalInfo(response)
            setLegalForm(toLegalForm(response))
        }
        catch (error) {
            if (error instanceof MyError && error.status === 404) {
                setLegalInfo(null)
                setLegalForm(EMPTY_LEGAL_INFO)
            }
            else {
                handleError(error, 'Не удалось получить юридические реквизиты')
            }
        }
        finally {
            setIsLegalLoading(false)
        }
    }, [handleError])

    const loadDomain = useCallback(async () => {
        try {
            setIsDomainLoading(true)
            const response = await legalInfoService.getAdminDomain()
            setDomain(response)
            setDomainForm(toDomainForm(response))
        }
        catch (error) {
            if (error instanceof MyError && error.status === 404) {
                setDomain(null)
                setDomainForm(EMPTY_DOMAIN)
            }
            else {
                handleError(error, 'Не удалось получить сведения о домене')
            }
        }
        finally {
            setIsDomainLoading(false)
        }
    }, [handleError])

    const loadData = useCallback(async () => {
        await Promise.all([loadLegalInfo(), loadDomain()])
    }, [loadDomain, loadLegalInfo])

    useEffect(() => {
        if (canReadLegalInfo) void loadData()
    }, [canReadLegalInfo, loadData])

    const startLegalEdit = () => {
        setLegalForm(toLegalForm(legalInfo))
        setIsLegalOpen(true)
        setIsLegalEditing(true)
    }

    const cancelLegalEdit = () => {
        setLegalForm(toLegalForm(legalInfo))
        setIsLegalEditing(false)
    }

    const startDomainEdit = () => {
        setDomainForm(toDomainForm(domain))
        setIsDomainOpen(true)
        setIsDomainEditing(true)
    }

    const cancelDomainEdit = () => {
        setDomainForm(toDomainForm(domain))
        setIsDomainEditing(false)
    }

    const saveLegalInfo = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        const patch = getLegalInfoPatch(legalInfo, legalForm)

        if (isEmpty(patch)) {
            cancelLegalEdit()
            return
        }

        try {
            setIsLegalSaving(true)
            const response = await legalInfoService.patchLegalInfo(patch)
            setLegalInfo(response)
            setLegalForm(toLegalForm(response))
            setIsLegalEditing(false)
            setGlobalMessage({message: 'Юридические реквизиты сохранены', type: 'ok'})
        }
        catch (error) {
            handleError(error, 'Не удалось сохранить юридические реквизиты')
        }
        finally {
            setIsLegalSaving(false)
        }
    }

    const saveDomain = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        const patch = getDomainPatch(domain, domainForm)

        if (isEmpty(patch)) {
            cancelDomainEdit()
            return
        }

        try {
            setIsDomainSaving(true)
            const response = await legalInfoService.patchAdminDomain(patch)
            setDomain(response)
            setDomainForm(toDomainForm(response))
            setIsDomainEditing(false)
            setGlobalMessage({message: 'Сведения о домене сохранены', type: 'ok'})
        }
        catch (error) {
            handleError(error, 'Не удалось сохранить сведения о домене')
        }
        finally {
            setIsDomainSaving(false)
        }
    }

    const requestDomainSync = async () => {
        if (!domain || isDomainSyncing) return

        try {
            setIsDomainSyncing(true)
            const response = await legalInfoService.requestAdminDomainSync()
            setDomain(response)
            setDomainForm(toDomainForm(response))
            setGlobalMessage({message: 'Синхронизация домена запущена', type: 'ok'})
        }
        catch (error) {
            handleError(error, 'Не удалось запустить синхронизацию домена')
        }
        finally {
            setIsDomainSyncing(false)
        }
    }

    if (!canOpen) {
        return <AccessDeniedRedirect message="Нет доступа к юридической информации" />
    }

    return (
        <LayoutPages title={LEGAL_INFO_ROUTE.name}>
            <section className={classes.page}>
                {canReadLegalInfo && <>
                <SurfaceBlock className={classes.card}>
                    <header className={`${classes.cardHeader} ${!isLegalOpen ? classes.cardHeaderCollapsed : ''}`}>
                        <section className={classes.heading}>
                            <span className={classes.icon}><ScalesIcon /></span>
                            <section>
                                <h1>Реквизиты ИП</h1>
                            </section>
                        </section>
                        <section className={classes.headerActions}>
                            {canManage && !isLegalEditing && !isLegalLoading && (
                                <button type="button" className={classes.editButton} onClick={startLegalEdit}>
                                    {legalInfo ? 'Редактировать' : 'Заполнить'}
                                </button>
                            )}
                            <button
                                type="button"
                                className={classes.collapseButton}
                                onClick={() => setIsLegalOpen(value => !value)}
                                disabled={isLegalEditing || isLegalSaving}
                                aria-expanded={isLegalOpen}
                                aria-label={isLegalOpen ? 'Свернуть реквизиты ИП' : 'Развернуть реквизиты ИП'}
                            >
                                <CollapseIcon isOpen={isLegalOpen} />
                            </button>
                        </section>
                    </header>

                    {isLegalOpen && (isLegalLoading ? (
                        <section className={classes.loading}>Загружаем реквизиты...</section>
                    ) : isLegalEditing ? (
                        <form className={classes.form} onSubmit={saveLegalInfo}>
                            <MyInput title="ИП" value={legalForm.fullName} setValue={fullName => setLegalForm(current => ({...current, fullName}))} required isLoading={isLegalSaving} />
                            <section className={classes.formGrid}>
                                <MyInput title="ИНН" value={legalForm.inn} setValue={inn => setLegalForm(current => ({...current, inn}))} inputMode="numeric" maxLength={12} pattern="[0-9]{12}" required isLoading={isLegalSaving} />
                                <MyInput title="ОГРНИП" value={legalForm.ogrnip} setValue={ogrnip => setLegalForm(current => ({...current, ogrnip}))} inputMode="numeric" maxLength={15} pattern="[0-9]{15}" required isLoading={isLegalSaving} />
                            </section>
                            <MyTextarea title="Адрес регистрации ИП" value={legalForm.registrationAddress} setValue={registrationAddress => setLegalForm(current => ({...current, registrationAddress}))} required disabled={isLegalSaving} />
                            <MyInput title="Адрес электронной почты" type="email" value={legalForm.email} setValue={email => setLegalForm(current => ({...current, email}))} required isLoading={isLegalSaving} />
                            <section className={classes.formActions}>
                                <MyButtonSecondary onClick={cancelLegalEdit} disabled={isLegalSaving}>Отмена</MyButtonSecondary>
                                <button type="submit" className={classes.primaryButton} disabled={isLegalSaving}>{isLegalSaving ? 'Сохраняем...' : 'Сохранить'}</button>
                            </section>
                        </form>
                    ) : legalInfo ? (
                        <section className={classes.infoGrid}>
                            <Field label="ИП" full>{legalInfo.fullName}</Field>
                            <Field label="ИНН">{legalInfo.inn}</Field>
                            <Field label="ОГРНИП">{legalInfo.ogrnip}</Field>
                            <Field label="Адрес регистрации ИП" full>{legalInfo.registrationAddress}</Field>
                            <Field label="Адрес электронной почты" full>{legalInfo.email}</Field>
                        </section>
                    ) : (
                        <section className={classes.empty}>Реквизиты ещё не заполнены.</section>
                    ))}
                </SurfaceBlock>

                <SurfaceBlock className={classes.card}>
                    <header className={`${classes.cardHeader} ${!isDomainOpen ? classes.cardHeaderCollapsed : ''}`}>
                        <section className={classes.heading}>
                            <span className={classes.icon}><DomainIcon /></span>
                            <section>
                                <h2>Домен</h2>
                            </section>
                        </section>
                        <section className={classes.headerActions}>
                            {canManage && !isDomainEditing && !isDomainLoading && (
                                <section className={classes.domainActions}>
                                    {domain && <button type="button" className={classes.primaryButton} onClick={requestDomainSync} disabled={isDomainSyncing}>
                                        {isDomainSyncing ? 'Запускаем...' : 'Запустить синхронизацию'}
                                    </button>}
                                    <button type="button" className={classes.editButton} onClick={startDomainEdit} disabled={isDomainSyncing}>
                                        {domain ? 'Редактировать' : 'Добавить домен'}
                                    </button>
                                </section>
                            )}
                            <button
                                type="button"
                                className={classes.collapseButton}
                                onClick={() => setIsDomainOpen(value => !value)}
                                disabled={isDomainEditing || isDomainSaving || isDomainSyncing}
                                aria-expanded={isDomainOpen}
                                aria-label={isDomainOpen ? 'Свернуть сведения о домене' : 'Развернуть сведения о домене'}
                            >
                                <CollapseIcon isOpen={isDomainOpen} />
                            </button>
                        </section>
                    </header>

                    {isDomainOpen && (isDomainLoading ? (
                        <section className={classes.loading}>Загружаем сведения о домене...</section>
                    ) : isDomainEditing ? (
                        <form className={classes.form} onSubmit={saveDomain}>
                            <section className={classes.formGrid}>
                                <MyInput title="Домен" value={domainForm.hostname} setValue={hostname => setDomainForm(current => ({...current, hostname}))} placeholder="school.example.ru" required isLoading={isDomainSaving} />
                                <label className={classes.selectField}>
                                    <span>Статус</span>
                                    <select value={domainForm.status} onChange={event => setDomainForm(current => ({...current, status: event.target.value as TAdminDomainStatus}))} disabled={isDomainSaving}>
                                        {DOMAIN_STATUS_OPTIONS.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
                                    </select>
                                </label>
                            </section>
                            <section className={classes.formGrid}>
                                <MyInput title="Подтверждение владения" type="datetime-local" value={domainForm.ownershipVerifiedAt} setValue={ownershipVerifiedAt => setDomainForm(current => ({...current, ownershipVerifiedAt}))} isLoading={isDomainSaving} />
                                <MyInput title="Домен оплачен до" type="date" value={domainForm.domainPaidUntil} setValue={domainPaidUntil => setDomainForm(current => ({...current, domainPaidUntil}))} isLoading={isDomainSaving} />
                                <MyInput title="Сертификат выдан" type="datetime-local" value={domainForm.certificateIssuedAt} setValue={certificateIssuedAt => setDomainForm(current => ({...current, certificateIssuedAt}))} isLoading={isDomainSaving} />
                                <MyInput title="Сертификат действует до" type="datetime-local" value={domainForm.certificateExpiresAt} setValue={certificateExpiresAt => setDomainForm(current => ({...current, certificateExpiresAt}))} isLoading={isDomainSaving} />
                            </section>
                            <section className={classes.formGrid}>
                                <MyInput title="Провайдер сертификата" value={domainForm.certificateProvider} setValue={certificateProvider => setDomainForm(current => ({...current, certificateProvider}))} isLoading={isDomainSaving} />
                                <MyInput title="Nginx применён" type="datetime-local" value={domainForm.nginxConfigAppliedAt} setValue={nginxConfigAppliedAt => setDomainForm(current => ({...current, nginxConfigAppliedAt}))} isLoading={isDomainSaving} />
                            </section>
                            <MyTextarea title="Последняя ошибка" value={domainForm.lastError} setValue={lastError => setDomainForm(current => ({...current, lastError}))} disabled={isDomainSaving} />
                            <section className={classes.formActions}>
                                <MyButtonSecondary onClick={cancelDomainEdit} disabled={isDomainSaving}>Отмена</MyButtonSecondary>
                                <button type="submit" className={classes.primaryButton} disabled={isDomainSaving}>{isDomainSaving ? 'Сохраняем...' : 'Сохранить'}</button>
                            </section>
                        </form>
                    ) : domain ? (
                        <section className={classes.infoGrid}>
                            <Field label="Домен" full>{domain.hostname}</Field>
                            <Field label="Статус"><span className={`${classes.status} ${classes[domain.status]}`}>{DOMAIN_STATUS_LABELS[domain.status]}</span></Field>
                            <Field label="Подтверждение владения">{formatDate(domain.ownershipVerifiedAt, true)}</Field>
                            <Field label="Домен оплачен до" highlighted>{formatDate(domain.domainPaidUntil)}</Field>
                            <Field label="Сертификат выдан">{formatDate(domain.certificateIssuedAt, true)}</Field>
                            <Field label="Сертификат действует до" highlighted>{formatDate(domain.certificateExpiresAt, true)}</Field>
                            <Field label="Провайдер сертификата">{domain.certificateProvider}</Field>
                            <Field label="Nginx применён">{formatDate(domain.nginxConfigAppliedAt, true)}</Field>
                            <Field label="Последняя ошибка" full>{domain.lastError}</Field>
                        </section>
                    ) : (
                        <section className={classes.empty}>Домен для этого admin-контура ещё не добавлен.</section>
                    ))}
                </SurfaceBlock>
                </>}
                {canReadLegalDocuments && <LegalDocumentsWidget />}
            </section>
        </LayoutPages>
    )
}
